import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Calendar, Clock, User, Zap } from "lucide-react";
import ReactMarkdown from "react-markdown";

const API = process.env.REACT_APP_BACKEND_URL;

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await fetch(`${API}/api/blog/posts/${slug}`);
        if (!response.ok) {
          if (response.status === 404) {
            setError("Post not found");
          } else {
            setError("Failed to load post");
          }
          return;
        }
        const data = await response.json();
        setPost(data);
      } catch (err) {
        console.error("Error fetching post:", err);
        setError("Failed to load post");
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <nav className="border-b border-slate-700/50 bg-slate-900/80 backdrop-blur-md">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <Link to="/blog" className="flex items-center gap-2 text-indigo-400 hover:text-indigo-500 transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Back to Blog
            </Link>
          </div>
        </nav>
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <h1 className="text-2xl font-bold text-indigo-500 mb-4">{error}</h1>
          <Link to="/blog" className="text-indigo-400 hover:text-indigo-400">
            Return to blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Navigation */}
      <nav className="border-b border-slate-700/50 bg-slate-900/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-500/20">
                <Zap className="w-5 h-5 text-indigo-500" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-indigo-500 tracking-tight">ClientKit</h1>
                <p className="text-xs text-slate-400 font-medium">Freelancer OS</p>
              </div>
            </Link>
            <Link 
              to="/blog"
              className="flex items-center gap-2 text-indigo-400 hover:text-indigo-500 transition-colors text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              All Posts
            </Link>
          </div>
        </div>
      </nav>

      {/* Article */}
      <article className="max-w-4xl mx-auto px-4 py-12">
        {/* Featured Image */}
        {post.featured_image_url && (
          <div className="mb-8 rounded-xl overflow-hidden">
            <img 
              src={post.featured_image_url} 
              alt={post.title}
              className="w-full h-64 sm:h-96 object-cover"
            />
          </div>
        )}

        {/* Header */}
        <header className="mb-8">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-indigo-500 mb-6 leading-tight">
            {post.title}
          </h1>
          
          <div className="flex flex-wrap items-center gap-4 text-sm text-indigo-400">
            <span className="flex items-center gap-2">
              <User className="w-4 h-4" />
              {post.author_name}
            </span>
            <span className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {new Date(post.published_at).toLocaleDateString('en-US', { 
                month: 'long', 
                day: 'numeric', 
                year: 'numeric' 
              })}
            </span>
            <span className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              {Math.ceil(post.content.split(/\s+/).length / 200)} min read
            </span>
          </div>
        </header>

        {/* Content */}
        <div className="prose pamber-invert pamber-lg max-w-none
          pamber-headings:text-indigo-500 pamber-headings:font-bold
          pamber-p:text-slate-400 pamber-p:leading-relaxed
          pamber-a:text-indigo-400 pamber-a:no-underline hover:pamber-a:text-indigo-400
          pamber-strong:text-indigo-500
          pamber-code:text-indigo-400 pamber-code:bg-slate-800 pamber-code:px-1 pamber-code:py-0.5 pamber-code:rounded
          pamber-pre:bg-slate-800 pamber-pre:border pamber-pre:border-slate-700
          pamber-blockquote:border-l-indigo-500 pamber-blockquote:bg-slate-800/50 pamber-blockquote:py-1 pamber-blockquote:px-4
          pamber-ul:text-slate-400 pamber-ol:text-slate-400
          pamber-li:marker:text-indigo-400
          pamber-img:rounded-xl
        ">
          <ReactMarkdown>{post.content}</ReactMarkdown>
        </div>

        {/* Author Box */}
        <div className="mt-12 p-6 bg-slate-800/50 border border-slate-700/50 rounded-xl">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-full flex items-center justify-center">
              <span className="text-indigo-500 font-bold text-lg">
                {post.author_name?.charAt(0) || 'C'}
              </span>
            </div>
            <div>
              <p className="text-indigo-500 font-semibold">{post.author_name}</p>
              <p className="text-indigo-400 text-sm">ClientKit Team</p>
            </div>
          </div>
        </div>

        {/* Back to Blog */}
        <div className="mt-8 pt-8 border-t border-slate-700">
          <Link 
            to="/blog"
            className="inline-flex items-center gap-2 text-indigo-400 hover:text-indigo-400 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to all posts
          </Link>
        </div>
      </article>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-slate-700/50">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-6 h-6 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-md flex items-center justify-center">
              <Zap className="w-3 h-3 text-indigo-500" />
            </div>
            <span className="text-slate-400 text-sm">ClientKit</span>
          </Link>
          
          {/* Social Links */}
          <div className="flex items-center gap-4">
            <a 
              href="https://x.com/yourfreelanceos" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-slate-400 hover:text-indigo-500 transition-colors"
              aria-label="Follow us on X"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
            </a>
            <a 
              href="https://www.instagram.com/yourfreelanceros/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-slate-400 hover:text-slate-400 transition-colors"
              aria-label="Follow us on Instagram"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
            </a>
            <a 
              href="https://www.facebook.com/profile.php?id=61586785478511" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-slate-400 hover:text-slate-400 transition-colors"
              aria-label="Follow us on Facebook"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </a>
          </div>
          
          <p className="text-slate-600 text-xs">
            © {new Date().getFullYear()} ClientKit. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
